import { useState } from 'react'
import { PageLoader } from '../../shared/ui/Spinner'
import { MyLoadPanel } from './procurement/MyLoadPanel'
import { ProcurementHeader } from './procurement/ProcurementHeader'
import { ProcurementKpiCards } from './procurement/ProcurementKpiCards'
import { ProcurementStatusBar } from './procurement/ProcurementStatusBar'
import { QuotesByDayChart } from './procurement/QuotesByDayChart'
import { RemindersPanel } from './procurement/RemindersPanel'
import { StaleItemsTable } from './procurement/StaleItemsTable'
import { SuppliersTable } from './procurement/SuppliersTable'
import { TopRequestedPartsTable } from './procurement/TopRequestedPartsTable'
import { PartPriceHistoryModal } from './procurement/modals/PartPriceHistoryModal'
import { ReminderModal } from './procurement/modals/ReminderModal'
import { StaleItemActionModal } from './procurement/modals/StaleItemActionModal'
import { SupplierDetailModal } from './procurement/modals/SupplierDetailModal'
import type { ProcurementAlert } from './procurement/types'
import {
  useProcurementDashboard,
  useProcurementPeriod,
} from './procurement/useProcurementDashboard'
import { NewsFeedWidget } from '../news/NewsFeedWidget'

type ModalState =
  | { type: 'supplier'; name: string }
  | { type: 'part'; partNumber: string; brand: string | null }
  | { type: 'stale'; itemId: string }
  | { type: 'alert'; alert: ProcurementAlert }
  | null

export function ProcurementDashboard() {
  const { state: period, setKey } = useProcurementPeriod()
  const { data, isLoading, isError, dataUpdatedAt, refetch } = useProcurementDashboard(period)
  const [modal, setModal] = useState<ModalState>(null)

  if (isLoading) return <PageLoader />
  if (isError || !data) {
    return (
      <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
        <p className="text-sm text-red-700 mb-2">Не удалось загрузить дашборд</p>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
        >
          Повторить
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <ProcurementHeader
        userName={data.user.name}
        primaryMatrix={data.user.primaryMatrix}
        period={period.key}
        onPeriodChange={setKey}
        fetchedAt={dataUpdatedAt}
      />

      <ProcurementKpiCards kpi={data.kpi} />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <QuotesByDayChart
            points={data.quotesChart.points}
            avgResponseHours={data.quotesChart.avgResponseHours}
          />
        </div>
        <div className="lg:col-span-2">
          <MyLoadPanel load={data.load} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SuppliersTable
          suppliers={data.suppliers}
          onRowClick={(name) => setModal({ type: 'supplier', name })}
        />
        <TopRequestedPartsTable
          topParts={data.topParts}
          onRowClick={(t) => setModal({ type: 'part', partNumber: t.partNumber, brand: t.brand })}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StaleItemsTable
          items={data.staleItems}
          onRowClick={(item) => setModal({ type: 'stale', itemId: item.itemId })}
        />
        <RemindersPanel
          alerts={data.alerts}
          onAlertClick={(alert) => setModal({ type: 'alert', alert })}
        />
      </div>

      <ProcurementStatusBar />

      <NewsFeedWidget />

      {modal?.type === 'supplier' && (
        <SupplierDetailModal supplierName={modal.name} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'part' && (
        <PartPriceHistoryModal
          target={{ partNumber: modal.partNumber, brand: modal.brand }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'stale' && (
        <StaleItemActionModal itemId={modal.itemId} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'alert' && (
        <ReminderModal alert={modal.alert} onClose={() => setModal(null)} />
      )}
    </div>
  )
}
